import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Form, Input, Button, message, Card, Typography, Alert } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CHANGE_PASSWORD_WITH_TOKEN } from '../../graphql/mutations/mutations';
import { AuthLayout } from '../../layouts/AuthLayout';

const { Title } = Typography;

interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

const ResetPasswordForm: React.FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const [changePassword] = useMutation(CHANGE_PASSWORD_WITH_TOKEN, {
    onCompleted: () => {
      message.success('Password has been updated successfully');
      setLoading(false);
      navigate('/login');
    },
    onError: error => {
      const errorMessage = error.graphQLErrors[0]?.message || 'Failed to reset password';
      setError(errorMessage);
      setLoading(false);
    },
  });

  const handleSubmit = (values: ResetPasswordFormValues) => {
    setLoading(true);
    setError(undefined);

    changePassword({
      variables: {
        newPassword: values.password,
        token,
      },
    });
  };

  return (
    <Card hoverable={false} style={{ width: '25rem', maxWidth: '90vw' }}>
      <Title level={2}>Reset your password</Title>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: '1.5rem' }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          password: '',
          confirmPassword: '',
        }}>
        <Form.Item
          name="password"
          label="New Password"
          rules={[
            { required: true, message: 'Please enter your new password' },
            { min: 8, message: 'Password must be at least 8 characters' },
            {
              pattern: /[0-9]/,
              message: 'Password must contain at least one number',
            },
            {
              pattern: /[a-z]/,
              message: 'Password must contain at least one lowercase letter',
            },
            {
              pattern: /[A-Z]/,
              message: 'Password must contain at least one uppercase letter',
            },
          ]}>
          <Input.Password placeholder="Enter new password" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Please confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}>
          <Input.Password placeholder="Confirm new password" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 'unset' }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ width: '100%', marginBottom: '10px' }}>
            Reset password
          </Button>
          Remember your password? <Link to="/login">Log in</Link>
        </Form.Item>
      </Form>
    </Card>
  );
};

export const ResetPassword: React.FC = () => {
  return (
    <AuthLayout>
      <ResetPasswordForm />
    </AuthLayout>
  );
};
